import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { AlertCircle, ThumbsUp, ThumbsDown, Lightbulb } from "lucide-react"
import { Pagination } from "@/components/pagination"
import { toast } from "sonner"
import { loadCrawlResults, convertToReportItem, type ReportItem } from "@/lib/crawl-data"

interface VerificationItem {
  id: string
  platform: string
  accountName: string
  accountUrl: string
  keywords: string[]
  confidence: number
  aiSuggestions: string[]
  content: string
  status: "pending" | "approved" | "rejected"
  metadata?: ReportItem['metadata']
}

const ITEMS_PER_PAGE = 3

export default function VerificationPage() {
  const [items, setItems] = useState<VerificationItem[]>([])
  const [selectedPlatform, setSelectedPlatform] = useState<string>("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedSuggestions, setExpandedSuggestions] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      const data = await loadCrawlResults()
      // Generate AI suggestions based on keywords
      const generateAISuggestions = (keywords: string[]): string[] => {
        const suggestions: string[] = []
        keywords.forEach(keyword => {
          const lowerKeyword = keyword.toLowerCase()
          if (lowerKeyword.includes('slot')) {
            suggestions.push('slot gacor', 'slot online', 'slot terpercaya')
          }
          if (lowerKeyword.includes('casino')) {
            suggestions.push('casino online', 'live casino', 'casino terbaik')
          }
          if (lowerKeyword.includes('togel')) {
            suggestions.push('togel online', 'toto togel', 'togel 4d')
          }
          if (lowerKeyword.includes('gacor')) {
            suggestions.push('gacor hari ini', 'slot gacor', 'gacor maxwin')
          }
          if (lowerKeyword.includes('depo')) {
            suggestions.push('depo minimal', 'depo cepat', 'depo tanpa potongan')
          }
          if (lowerKeyword.includes('zeus')) {
            suggestions.push('zeus gacor', 'gates of olympus', 'zeus maxwin')
          }
        })
        return suggestions.length > 0 ? suggestions : ['keyword terkait', 'variasi keyword']
      }

      const items: VerificationItem[] = data.results
        .map((result, index) => convertToReportItem(result, index))
        .filter(result => result.confidence > 0.3) // Filter by confidence
        .slice(0, 20) // Limit to 20 items
        .map((result) => ({
          id: result.id,
          platform: result.platform,
          accountName: result.accountName,
          accountUrl: result.accountUrl,
          keywords: result.detectedKeywords,
          confidence: result.confidence,
          aiSuggestions: generateAISuggestions(result.detectedKeywords),
          content: result.content || `Content from ${result.accountName} with detected gambling keywords.`,
          status: "pending" as const,
          metadata: result.metadata,
        }))
      setItems(items)
    }
    fetchData()
  }, [])

  const platforms = ["all", ...Array.from(new Set(items.map((i) => i.platform)))]

  const filteredItems = items.filter((item) => {
    if (selectedPlatform === "all") return true
    return item.platform === selectedPlatform
  })

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE)
  const paginatedItems = filteredItems.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  const handleApprove = (id: string) => {
    const item = items.find(i => i.id === id)
    const updated = items.map((item) => (item.id === id ? { ...item, status: "approved" as const } : item))
    setItems(updated)
    toast.success("Item approved", {
      description: `${item?.accountName} has been approved and moved to broadcast queue`,
    })
  }

  const handleReject = (id: string) => {
    const item = items.find(i => i.id === id)
    const updated = items.map((item) => (item.id === id ? { ...item, status: "rejected" as const } : item))
    setItems(updated)
    toast.error("Item rejected", {
      description: `${item?.accountName} has been rejected`,
    })
  }

  const handleAddSuggestion = (suggestion: string) => {
    console.log("Adding suggestion to dictionary:", suggestion)
  }

  const pendingCount = items.filter((i) => i.status === "pending").length
  const approvedCount = items.filter((i) => i.status === "approved").length
  const rejectedCount = items.filter((i) => i.status === "rejected").length

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Verification System</h1>
        <p className="text-muted-foreground mt-2">Verifikasi akun per platform dan dapatkan AI suggestions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
              <p className="text-sm text-muted-foreground">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
              <p className="text-sm text-muted-foreground">Rejected</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2">
        {platforms.map((platform) => (
          <Button
            key={platform}
            variant={selectedPlatform === platform ? "default" : "outline"}
            onClick={() => {
              setSelectedPlatform(platform)
              setCurrentPage(1)
            }}
            className={selectedPlatform === platform ? "bg-primary text-primary-foreground" : ""}
          >
            {platform === "all" ? "All Platforms" : platform}
          </Button>
        ))}
      </div>

      {/* Verification Items List */}
      <div className="space-y-4">
        {paginatedItems.map((item) => (
          <Card key={item.id} className="border-2 border-primary/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-primary" />
                    {item.accountName}
                  </CardTitle>
                  <CardDescription>{item.accountUrl}</CardDescription>
                </div>
                <Badge className="bg-primary text-primary-foreground">{item.platform}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Content Preview */}
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-2">Content Preview</p>
                <p className="text-foreground italic">"{item.content}"</p>
              </div>

              {/* Detected Keywords */}
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Detected Keywords</p>
                <div className="flex flex-wrap gap-2">
                  {item.keywords.map((keyword, idx) => (
                    <Badge key={idx} variant="secondary">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Confidence Score */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Confidence Score</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div className="bg-primary h-3 rounded-full" style={{ width: `${item.confidence * 100}%` }} />
                  </div>
                  <span className="font-bold text-lg">{(item.confidence * 100).toFixed(0)}%</span>
                </div>
              </div>

              {/* AI Suggestions */}
              <div className="border-t pt-4">
                <button
                  onClick={() => setExpandedSuggestions(expandedSuggestions === item.id ? null : item.id)}
                  className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium"
                >
                  <Lightbulb className="h-4 w-4" />
                  AI Suggestions for Dictionary
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full ml-2">
                    {item.aiSuggestions.length}
                  </span>
                </button>

                {expandedSuggestions === item.id && (
                  <div className="mt-4 space-y-2">
                    {item.aiSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20"
                      >
                        <span className="text-foreground">{suggestion}</span>
                        <Button size="sm" variant="outline" onClick={() => handleAddSuggestion(suggestion)}>
                          Add to Dictionary
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => handleReject(item.id)}
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <ThumbsDown className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button onClick={() => handleApprove(item.id)} className="flex-1 bg-green-600 hover:bg-green-700">
                  <ThumbsUp className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center py-4">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  )
}

